import {
  LivenessWebVerifyStatusEnum,
  RegtankLivenessFHStatusEnum,
  RegtankOnboardingFHStatusEnum,
} from "@funded-here-interface/common/src/constant/enum";
import { RegtankOnboardingStatusEnum } from "@funded-here-interface/common/src/constant/enum";

export interface resultOnboardingChecking {
  matchRequestId: string;
  newStatus: RegtankOnboardingFHStatusEnum;
  newAttemptUsed: string;
  disabledPhotoVidBtn: boolean;
  disabledDocumentBtn: boolean;
  hiddenVerificationHistoryData: boolean;
  newVerificationHistoryStatus: string;
  newReviewedOn: string;
  newSubmittedOn: string;
  newFailureReason: string;
  newReattemptApproval: string;
  newRemark: string;
}

export interface resultLivenessChecking {
  matchSessionId: string;
  newStatus: RegtankLivenessFHStatusEnum;
  hiddenLivenessHistoryData: boolean;
  newVerificationResult: string;
  disabledPhotoVidBtn: boolean;
  newReviewedOn: string;
  newFailureReason: string;
  newRemark: string;
  newReattemptApproval: string;
  newSubmittedOn: string;
}

// Constants for common default values
const DASH = "-";
const EMPTY_STRING = "";

// Common field sets for status results
const PASSED_DEFAULTS = {
  newReviewedOn: DASH,
  newFailureReason: DASH,
  newRemark: DASH,
  newReattemptApproval: DASH,
  newVerificationResult: "PASSED",
};

export const getRegtankColor = (status: any): string => {
  switch (status) {
    case RegtankOnboardingFHStatusEnum.PASSED:
    case RegtankLivenessFHStatusEnum.PASSED:
      return "green";
    case RegtankOnboardingFHStatusEnum.PENDING_REVIEW:
      return "yellow";
    case RegtankOnboardingFHStatusEnum.PENDING_REATTEMPT:
    case RegtankLivenessFHStatusEnum.PENDING_REATTEMPT:
      return "blue";
    case RegtankOnboardingFHStatusEnum.REATTEMPT_REJECTED:
    case RegtankLivenessFHStatusEnum.REATTEMPT_REJECTED:
      return "red";
    case RegtankOnboardingFHStatusEnum.PENDING_VERIFICATION:
    case RegtankLivenessFHStatusEnum.NOTHING:
      return "gray";
    default:
      return "gray";
  }
};

/**
 * Helper to create base result object with default values
 */
const createBaseOnboardingResult = (
  requestId: string,
  attemptUsed: number,
  submittedOn?: string,
  reason?: string
): resultOnboardingChecking => ({
  matchRequestId: requestId,
  newStatus: RegtankOnboardingFHStatusEnum.PENDING_VERIFICATION,
  newAttemptUsed: attemptUsed === 0 ? EMPTY_STRING : attemptUsed.toString(),
  disabledPhotoVidBtn: true,
  disabledDocumentBtn: true,
  hiddenVerificationHistoryData: false,
  newVerificationHistoryStatus: EMPTY_STRING,
  newReviewedOn: EMPTY_STRING,
  newSubmittedOn: submittedOn || EMPTY_STRING,
  newFailureReason: reason || EMPTY_STRING,
  newReattemptApproval: EMPTY_STRING,
  newRemark: EMPTY_STRING,
});

/**
 * Handle reattempt approval logic (APPROVED/REJECTED)
 */
const handleReattemptApproval = (
  requestId: string,
  reattemptApproval: string,
  onboardingStatus: string,
  attemptUsed: number,
  reviewedOn?: string,
  submittedOn?: string,
  reason?: string,
  remark?: string,
  documentIdSubmittedDate?: string
): resultOnboardingChecking | null => {
  if (onboardingStatus === RegtankOnboardingStatusEnum.APPROVED) {
    return null; // Skip reattempt logic if already approved
  }

  const baseResult = createBaseOnboardingResult(
    requestId,
    attemptUsed,
    submittedOn
  );

  if (reattemptApproval === "APPROVED") {
    return {
      ...baseResult,
      newStatus: RegtankOnboardingFHStatusEnum.PENDING_REATTEMPT,
      newAttemptUsed: (attemptUsed + 1).toString(),
      newReviewedOn: reviewedOn || EMPTY_STRING,
      newSubmittedOn: submittedOn || EMPTY_STRING,
      newFailureReason: reason || EMPTY_STRING,
      newReattemptApproval: reattemptApproval,
      newRemark: remark || EMPTY_STRING,
      newVerificationHistoryStatus: submittedOn ? "FAILED" : "EXPIRED",
      disabledDocumentBtn: documentIdSubmittedDate ? false : true,
      disabledPhotoVidBtn: submittedOn ? false : true,
    };
  }

  if (reattemptApproval === "REJECTED") {
    return {
      ...baseResult,
      newStatus: RegtankOnboardingFHStatusEnum.REATTEMPT_REJECTED,
      newAttemptUsed: (attemptUsed + 1).toString(),
      newVerificationHistoryStatus: submittedOn ? "FAILED" : "EXPIRED",
      disabledDocumentBtn: documentIdSubmittedDate ? false : true,
      disabledPhotoVidBtn: submittedOn ? false : true,
      newFailureReason: reason || EMPTY_STRING,
    };
  }

  return null;
};

/**
 * Process status for APPROVED cases
 */
const handleApprovedStatus = (
  baseResult: resultOnboardingChecking,
  attemptUsed: number,
  incrementAttempt: boolean
): Partial<resultOnboardingChecking> => ({
  newStatus: RegtankOnboardingFHStatusEnum.PASSED,
  newVerificationHistoryStatus: "PASSED",
  newAttemptUsed: incrementAttempt
    ? (attemptUsed + 1).toString()
    : attemptUsed === 0
    ? "1"
    : baseResult.newAttemptUsed,
  disabledPhotoVidBtn: false,
  disabledDocumentBtn: false,
  ...PASSED_DEFAULTS,
});

/**
 * Process status for REJECTED cases
 */
const handleRejectedStatus = (
  attemptUsed: number,
  incrementAttempt: boolean
): Partial<resultOnboardingChecking> => ({
  newStatus: RegtankOnboardingFHStatusEnum.PENDING_REVIEW,
  newVerificationHistoryStatus: "FAILED",
  newAttemptUsed: incrementAttempt ? (attemptUsed + 1).toString() : "1",
  disabledDocumentBtn: false,
  disabledPhotoVidBtn: false,
});

/**
 * Process status for EXPIRED cases (ID_UPLOADED, LIVENESS_STARTED)
 */
const handleExpiredStatus = (
  disabledDocumentBtn: boolean,
  disabledPhotoVidBtn: boolean
): Partial<resultOnboardingChecking> => ({
  newStatus: RegtankOnboardingFHStatusEnum.PENDING_REATTEMPT,
  newVerificationHistoryStatus: "EXPIRED",
  newSubmittedOn: DASH,
  disabledDocumentBtn,
  disabledPhotoVidBtn,
  ...PASSED_DEFAULTS,
});

/**
 * Process status changes based on onboarding status
 */
const processStatusChange = (
  onboardingStatus: string,
  baseResult: resultOnboardingChecking,
  attemptUsed: number,
  isBeforeExpiry: boolean,
  documentIdSubmittedDate?: string,
  submittedDate?: string
): Partial<resultOnboardingChecking> => {
  const incrementAttempt = attemptUsed > 0;
  const disabledDocumentBtn = documentIdSubmittedDate ? false : true;
  const disabledPhotoVidBtn = submittedDate ? false : true;
  switch (onboardingStatus) {
    case RegtankOnboardingStatusEnum.APPROVED:
      return handleApprovedStatus(baseResult, attemptUsed, incrementAttempt);

    case RegtankOnboardingStatusEnum.REJECTED:
      return handleRejectedStatus(attemptUsed, incrementAttempt);

    case RegtankOnboardingStatusEnum.EXPIRED:
      return disabledDocumentBtn === false
        ? {
            disabledDocumentBtn,
            newSubmittedOn: DASH,
            newReviewedOn: DASH,
            disabledPhotoVidBtn,
            newVerificationHistoryStatus: "EXPIRED",
            newFailureReason: DASH,
            newRemark: DASH,
            newReattemptApproval: DASH,
          }
        : {
            hiddenVerificationHistoryData: true,
          };
    case RegtankOnboardingStatusEnum.PROCESSING:
      if (!isBeforeExpiry) {
        //ALREADY EXPIRED
        return attemptUsed === 0
          ? {
              hiddenVerificationHistoryData: true,
              disabledDocumentBtn,
            }
          : {
              newStatus: RegtankOnboardingFHStatusEnum.PENDING_REATTEMPT,
              hiddenVerificationHistoryData: true,
              disabledDocumentBtn,
            };
      }
      return isBeforeExpiry
        ? { disabledDocumentBtn }
        : { hiddenVerificationHistoryData: true, disabledDocumentBtn };

    case RegtankOnboardingStatusEnum.ID_UPLOADED:
    case RegtankOnboardingStatusEnum.LIVENESS_STARTED:
      return !isBeforeExpiry
        ? handleExpiredStatus(disabledDocumentBtn, disabledPhotoVidBtn)
        : {
            disabledDocumentBtn,
            disabledPhotoVidBtn,
            newSubmittedOn: EMPTY_STRING,
          };

    case RegtankOnboardingStatusEnum.ID_UPLOADED_FAILED:
      return !isBeforeExpiry
        ? {
            newStatus: RegtankOnboardingFHStatusEnum.PENDING_REATTEMPT,
            hiddenVerificationHistoryData: true,
            disabledDocumentBtn,
          }
        : {};

    case RegtankOnboardingStatusEnum.LIVENESS_FAILED:
      if (isBeforeExpiry) {
        return attemptUsed > 0
          ? {
              newSubmittedOn: EMPTY_STRING,
              disabledDocumentBtn,
              disabledPhotoVidBtn: true,
              newFailureReason: EMPTY_STRING,
            }
          : {
              newSubmittedOn: EMPTY_STRING,
              newAttemptUsed: EMPTY_STRING,
              disabledDocumentBtn,
              disabledPhotoVidBtn: true,
              newFailureReason: EMPTY_STRING,
            };
      }
      return {
        newStatus: RegtankOnboardingFHStatusEnum.PENDING_REVIEW,
        newAttemptUsed: attemptUsed === 0 ? "1" : (attemptUsed + 1).toString(),
        disabledPhotoVidBtn,
        disabledDocumentBtn,
        newVerificationHistoryStatus: "FAILED",
      };

    default:
      return {};
  }
};

export const changeRegtankStatusAttemptUsed = (
  requestId: string,
  onboardingStatus: string,
  expiredDate: string,
  reattemptApproval: string,
  attemptUsed: number,
  submittedOn?: string,
  reviewedOn?: string,
  remark?: string,
  reason?: string,
  documentIdSubmittedDate?: string
): resultOnboardingChecking => {
  // Handle reattempt approval first
  const reattemptResult = handleReattemptApproval(
    requestId,
    reattemptApproval,
    onboardingStatus,
    attemptUsed,
    reviewedOn,
    submittedOn,
    reason,
    remark,
    documentIdSubmittedDate
  );
  if (reattemptResult) return reattemptResult;

  // Create base result
  const baseResult = createBaseOnboardingResult(
    requestId,
    attemptUsed,
    submittedOn,
    reason
  );

  // Check if before expiry
  const isBeforeExpiry = expiredDate >= new Date().toISOString();

  // Process status changes
  const statusChanges = processStatusChange(
    onboardingStatus,
    baseResult,
    attemptUsed,
    isBeforeExpiry,
    documentIdSubmittedDate,
    submittedOn
  );

  return { ...baseResult, ...statusChanges };
};

/**
 * Generic reattempt approval handler for both onboarding and liveness
 */
const handleGenericReattemptApproval = <T extends string>(
  reattemptApproval: string,
  verificationResult: string,
  failedStatuses: string[],
  pendingReattemptStatus: T,
  reattemptRejectedStatus: T,
  defaultStatus: T
): T => {
  const isFailed = failedStatuses.includes(verificationResult);

  if (reattemptApproval === "APPROVED" && isFailed) {
    return pendingReattemptStatus;
  }
  if (reattemptApproval === "REJECTED" && isFailed) {
    return reattemptRejectedStatus;
  }
  return defaultStatus;
};

export const changeStatusAfterReattemptApproval = (data: any) => {
  return handleGenericReattemptApproval(
    data.reattemptApproval,
    data.verificationResult,
    [
      RegtankOnboardingStatusEnum.REJECTED,
      RegtankOnboardingStatusEnum.LIVENESS_FAILED,
    ],
    RegtankOnboardingFHStatusEnum.PENDING_REATTEMPT,
    RegtankOnboardingFHStatusEnum.REATTEMPT_REJECTED,
    RegtankOnboardingFHStatusEnum.PENDING_REVIEW
  );
};

/**
 * Helper to create base liveness result
 */
const createBaseLivenessResult = (
  sessionId: string
): resultLivenessChecking => ({
  matchSessionId: sessionId,
  newStatus: RegtankLivenessFHStatusEnum.NOTHING,
  hiddenLivenessHistoryData: false,
  newVerificationResult: EMPTY_STRING,
  disabledPhotoVidBtn: true,
  newReviewedOn: EMPTY_STRING,
  newFailureReason: EMPTY_STRING,
  newRemark: EMPTY_STRING,
  newReattemptApproval: EMPTY_STRING,
  newSubmittedOn: EMPTY_STRING,
});

/**
 * Handle liveness reattempt approval
 */
const handleLivenessReattemptApproval = (
  sessionId: string,
  reattemptApproval: string,
  submittedOn: string,
  failureReason: string
): resultLivenessChecking | null => {
  const baseResult = createBaseLivenessResult(sessionId);

  if (reattemptApproval === "APPROVED") {
    return {
      ...baseResult,
      newStatus: RegtankLivenessFHStatusEnum.PENDING_REATTEMPT,
      newVerificationResult: "PASSED",
      disabledPhotoVidBtn: false,
      newFailureReason: failureReason,
      newReattemptApproval: reattemptApproval,
      newSubmittedOn: submittedOn,
    };
  }

  if (reattemptApproval === "REJECTED") {
    return {
      ...baseResult,
      newStatus: RegtankLivenessFHStatusEnum.REATTEMPT_REJECTED,
      newVerificationResult: "FAILED",
      disabledPhotoVidBtn: false,
      newFailureReason: failureReason,
      newReattemptApproval: reattemptApproval,
      newSubmittedOn: submittedOn,
    };
  }

  return null;
};

/**
 * Process liveness status changes
 */
const processLivenessStatus = (
  livenessStatus: LivenessWebVerifyStatusEnum | null,
  isNotExpired: boolean,
  numberOfLiveness: number,
  submittedOn: string,
  failureReason: string
): Partial<resultLivenessChecking> => {
  // Handle null or LIVENESS_FAILED before expiry
  if (
    isNotExpired &&
    (livenessStatus === null ||
      livenessStatus === LivenessWebVerifyStatusEnum.LIVENESS_FAILED)
  ) {
    return numberOfLiveness > 0
      ? {
          newStatus: RegtankLivenessFHStatusEnum.PENDING_REATTEMPT,
          newVerificationResult: EMPTY_STRING,
          newSubmittedOn: EMPTY_STRING,
        }
      : { newVerificationResult: EMPTY_STRING, newSubmittedOn: EMPTY_STRING };
  }

  // Handle null after expiry
  if (!isNotExpired && livenessStatus === null) {
    return numberOfLiveness > 0
      ? {
          newStatus: RegtankLivenessFHStatusEnum.PENDING_REATTEMPT,
          hiddenLivenessHistoryData: true,
        }
      : { hiddenLivenessHistoryData: true };
  }

  // Handle specific liveness statuses
  switch (livenessStatus) {
    case LivenessWebVerifyStatusEnum.SUCCESS:
      return {
        newStatus: RegtankLivenessFHStatusEnum.PASSED,
        disabledPhotoVidBtn: false,
        newSubmittedOn: submittedOn,
        ...PASSED_DEFAULTS,
      };

    case LivenessWebVerifyStatusEnum.FAILED:
      return {
        newStatus: RegtankLivenessFHStatusEnum.PENDING_REVIEW,
        disabledPhotoVidBtn: false,
        newSubmittedOn: submittedOn,
        newFailureReason: failureReason,
        ...(isNotExpired ? {} : { newVerificationResult: "FAILED" }),
      };

    case LivenessWebVerifyStatusEnum.LIVENESS_FAILED:
      return !isNotExpired
        ? {
            newStatus: RegtankLivenessFHStatusEnum.PENDING_REVIEW,
            newVerificationResult: "FAILED",
            disabledPhotoVidBtn: false,
            newFailureReason: failureReason,
          }
        : {};

    default:
      return {};
  }
};

export const changeRegtankLivenessData = (
  sessionId: string,
  livenessStatus: LivenessWebVerifyStatusEnum,
  expiredDate: string,
  numberOfLiveness: number,
  reattemptApproval: string,
  submittedOn: string,
  failureReason: string
): resultLivenessChecking => {
  // Handle reattempt approval first
  const reattemptResult = handleLivenessReattemptApproval(
    sessionId,
    reattemptApproval,
    submittedOn,
    failureReason
  );
  if (reattemptResult) return reattemptResult;

  // Create base result
  const baseResult = createBaseLivenessResult(sessionId);
  baseResult.newSubmittedOn = submittedOn;

  // Check if not expired
  const isNotExpired = expiredDate >= new Date().toISOString();

  // Process liveness status
  const statusChanges = processLivenessStatus(
    livenessStatus,
    isNotExpired,
    numberOfLiveness,
    submittedOn,
    failureReason
  );

  return { ...baseResult, ...statusChanges };
};

export const changeStatusLivenessReattemptApproval = (data: any) => {
  return handleGenericReattemptApproval(
    data.reattemptApproval,
    data.verificationResult,
    [
      LivenessWebVerifyStatusEnum.FAILED,
      LivenessWebVerifyStatusEnum.LIVENESS_FAILED,
    ],
    RegtankLivenessFHStatusEnum.PENDING_REATTEMPT,
    RegtankLivenessFHStatusEnum.REATTEMPT_REJECTED,
    RegtankLivenessFHStatusEnum.NOTHING
  );
};
